import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { CursorPagination } from '../../../../common/pagination/CursorPagination'
import { getErrorResponse } from '../../../../common/api/errors'
import { CopyableCell } from '../../../../common/CopyableCell'
import { PayoutTokenLink } from '../../../../common/PayoutTokenLink'
import { DepositTokenLink } from '../../../../common/DepositTokenLink'
import { getListMerchantsForSelect } from '../../../../common/core/_requests'
import { useAuth } from '../../../auth'
import { convertUTCtoMSK } from '../../../../../utils'
import { fetchAuditLogs } from './core/api'
import { breadCrumbs, SEARCH_FIELD_OPTIONS } from './core/consts'
import { DEFAULT_AUDIT_FILTERS, DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from './core/query.defaults'
import { IAuditFilters } from './core/types'
import { IAuditLogRecord } from './core/response.types'
import { isPayoutTicketRecord, isPaymentTicketRecord } from './core/ticketType'

const URL_PARAM_TO_FIELD: Record<string, 'ticket_token' | 'transaction_id' | 'payment_id'> = {
  ticket_token: 'ticket_token',
  transaction_id: 'transaction_id',
  payment_id: 'payment_id',
}

const tryFormatJson = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

const ctx = (rec: IAuditLogRecord): Record<string, any> => rec.context ?? {}

const PayloadBlock: React.FC<{ label: string; content: string | null; height?: number }> = ({
  label,
  content,
  height = 300,
}) => {
  const [copied, setCopied] = useState(false)

  if (!content) {
    return (
      <div>
        <div className='fw-bold mb-2'>{label}</div>
        <div className='text-muted fst-italic'>—</div>
      </div>
    )
  }

  const formatted = tryFormatJson(content)

  const handleCopy = () => {
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <span className='fw-bold'>{label}</span>
        <button
          type='button'
          className='btn btn-sm btn-light-primary py-1 px-3'
          onClick={handleCopy}
        >
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      <pre
        style={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          background: 'var(--bs-secondary-bg)',
          border: '1px solid var(--bs-border-color)',
          borderRadius: '6px',
          padding: '12px',
          maxHeight: `${height}px`,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          margin: 0,
          color: 'var(--bs-body-color)',
        }}
      >
        {formatted}
      </pre>
    </div>
  )
}

const StatusBadge: React.FC<{ code: number | null }> = ({ code }) => {
  if (!code) return <span>—</span>
  const isOk = code >= 200 && code < 300
  return (
    <span className={`badge ${isOk ? 'badge-light-success' : 'badge-light-danger'}`}>
      {isOk ? 'OK' : 'ERROR'}
    </span>
  )
}

export const AuditPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { currentUser } = useAuth()

  const initialFilters = useMemo<IAuditFilters>(() => {
    for (const param of Object.keys(URL_PARAM_TO_FIELD)) {
      const value = searchParams.get(param)
      if (value && value.trim() !== '') {
        return {
          ...DEFAULT_AUDIT_FILTERS,
          searchAdvanced: { field: URL_PARAM_TO_FIELD[param], query: value },
        }
      }
    }
    return DEFAULT_AUDIT_FILTERS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [data, setData] = useState<IAuditLogRecord[]>([])
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE)
  // Стек курсоров уже показанных страниц: cursors[0] === undefined (первая страница),
  // cursorIndex — текущая позиция. Смена фильтров или размера страницы сбрасывает стек —
  // иначе «Назад» уводил бы на страницу прошлой выборки.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [cursorIndex, setCursorIndex] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [activeFilters, setActiveFilters] = useState<IAuditFilters>(initialFilters)
  const [selectedRecord, setSelectedRecord] = useState<IAuditLogRecord | null>(null)
  const [merchantOptions, setMerchantOptions] = useState<{ value: string; label: string }[]>([])

  useEffect(() => {
    getListMerchantsForSelect(currentUser)
      .then((response) => {
        if (!response.data.success || !response.data.merchants) return
        setMerchantOptions(response.data.merchants.map((m) => ({ value: m.title, label: m.title })))
      })
      .catch(() => setMerchantOptions([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = useCallback(
    async (filters: IAuditFilters, size: number, cursor: string | undefined) => {
      setIsLoading(true)
      try {
        const searchField = filters.searchAdvanced?.field
        const searchValue = filters.searchAdvanced?.query?.trim() || undefined

        const response = await fetchAuditLogs({
          date_from: filters.dateStart?.toISOString() || undefined,
          date_to: filters.dateEnd?.toISOString() || undefined,
          ticket_token: searchField === 'ticket_token' ? searchValue : undefined,
          payment_id: searchField === 'payment_id' ? searchValue : undefined,
          transaction_id: searchField === 'transaction_id' ? searchValue : undefined,
          merchant_name: filters.merchant?.trim() || undefined,
          cursor,
          per_page: size,
        })
        if (!response?.data?.success) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }

        setData(response.data.data ?? [])
        setNextCursor(response.data.nextCursor ?? null)
      } catch (error: any) {
        const message =
          error?.message ||
          getErrorResponse<{ general_error?: string }>(error)?.general_error ||
          'Не удалось загрузить логи аудита'
        Swal.fire({ title: 'Ошибка', text: message, icon: 'error' })
        setData([])
        setNextCursor(null)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    loadData(initialFilters, DEFAULT_PER_PAGE, undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetCursors = () => {
    setCursors([undefined])
    setCursorIndex(0)
  }

  const handleApplyFilters = (filters: IAuditFilters) => {
    setActiveFilters(filters)
    resetCursors()
    loadData(filters, pageSize, undefined)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    resetCursors()
    loadData(activeFilters, size, undefined)
  }

  const handleForward = () => {
    if (!nextCursor) return
    const nextIndex = cursorIndex + 1
    setCursors((prev) => {
      const copy = prev.slice(0, nextIndex)
      copy[nextIndex] = nextCursor
      return copy
    })
    setCursorIndex(nextIndex)
    loadData(activeFilters, pageSize, nextCursor)
  }

  const handleBack = () => {
    if (cursorIndex === 0) return
    const prevIndex = cursorIndex - 1
    setCursorIndex(prevIndex)
    loadData(activeFilters, pageSize, cursors[prevIndex])
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Аудит интеграций</span>
          </h3>
        </div>
      </div>

      <FilterManager<IAuditFilters>
        activeFilters={['searchAdvanced', { filter: 'select', fieldName: 'merchant' }]}
        defaultValues={initialFilters}
        onApplyFilters={handleApplyFilters}
        filterProps={{
          searchAdvanced: {
            options: SEARCH_FIELD_OPTIONS,
            placeholder: 'Введите значение для поиска...',
          },
          merchant: {
            label: 'Мерчант',
            options: merchantOptions,
            placeholder: 'Все мерчанты',
          },
        }}
      />

      <div className='card'>
        <div className='card-body p-0'>
          <div className='table-responsive'>
            <table className='table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th>ID тикета</th>
                  <th>Мерчант</th>
                  <th>ID транзакции</th>
                  <th>Адаптер</th>
                  <th>ID адаптера</th>
                  <th>Создан</th>
                  <th>Направление</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={9} className='text-center py-10'>
                      Загрузка...
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={9} className='text-center py-10 text-muted'>
                      Нет данных
                    </td>
                  </tr>
                ) : (
                  data.map((rec) => (
                    <tr key={rec.id}>
                      <td>
                        <CopyableCell
                          value={ctx(rec).ticket_token}
                          mono
                          renderValue={
                            isPayoutTicketRecord(rec)
                              ? (token) => <PayoutTokenLink token={token} />
                              : isPaymentTicketRecord(rec)
                              ? (token) => <DepositTokenLink token={token} />
                              : undefined
                          }
                        />
                      </td>
                      <td>{ctx(rec).merchant_name ?? '—'}</td>
                      <td>
                        <CopyableCell value={rec.transaction_id} />
                      </td>
                      <td>{ctx(rec).adapter_name ?? '—'}</td>
                      <td>
                        <CopyableCell value={rec.payment_id} />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{convertUTCtoMSK(rec.created_at)}</td>
                      <td>
                        <span
                          className={`badge ${
                            rec.direction === 'in' ? 'badge-light-info' : 'badge-light-primary'
                          }`}
                        >
                          {rec.direction === 'in' ? '← входящий' : '→ исходящий'}
                        </span>
                      </td>
                      <td>
                        <StatusBadge code={rec.status_code} />
                      </td>
                      <td>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          onClick={() => setSelectedRecord(rec)}
                        >
                          Подробнее
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className='px-5 pb-5'>
            <CursorPagination
              pageSize={pageSize}
              pageSizeOptions={PER_PAGE_OPTIONS}
              canGoBack={cursorIndex > 0}
              canGoForward={nextCursor !== null}
              onBack={handleBack}
              onForward={handleForward}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      <Modal
        show={selectedRecord !== null}
        onHide={() => setSelectedRecord(null)}
        size='xl'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='fs-5 fw-bold'>
            Аудит #{selectedRecord?.id} &mdash;{' '}
            {selectedRecord?.direction === 'in' ? '← входящий' : '→ исходящий'}{' '}
            {selectedRecord?.kind || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div className='d-flex flex-column gap-5'>
              <div className='row g-4'>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted mb-1'>Дата</div>
                  <div>{convertUTCtoMSK(selectedRecord.created_at)}</div>
                </div>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted mb-1'>Направление</div>
                  <span
                    className={`badge ${
                      selectedRecord.direction === 'in' ? 'badge-light-info' : 'badge-light-primary'
                    }`}
                  >
                    {selectedRecord.direction === 'in' ? '← входящий' : '→ исходящий'}
                  </span>
                </div>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted mb-1'>Статус</div>
                  {selectedRecord.status_code !== null ? (
                    <span
                      className={`badge ${
                        selectedRecord.status_code >= 200 && selectedRecord.status_code < 300
                          ? 'badge-light-success'
                          : 'badge-light-danger'
                      }`}
                    >
                      {selectedRecord.status_code >= 200 && selectedRecord.status_code < 300
                        ? `OK (${selectedRecord.status_code})`
                        : `ERROR (${selectedRecord.status_code})`}
                    </span>
                  ) : (
                    '—'
                  )}
                </div>
                {selectedRecord.duration_ms !== null && (
                  <div className='col-md-4'>
                    <div className='fw-bold text-muted mb-1'>Длительность</div>
                    <div>{(selectedRecord.duration_ms / 1000).toFixed(3)} с</div>
                  </div>
                )}
                <div className='col-md-4'>
                  <div className='fw-bold text-muted mb-1'>ID тикета</div>
                  <div
                    style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                  >
                    {isPayoutTicketRecord(selectedRecord) ? (
                      <PayoutTokenLink token={ctx(selectedRecord).ticket_token} empty='—' />
                    ) : isPaymentTicketRecord(selectedRecord) ? (
                      <DepositTokenLink token={ctx(selectedRecord).ticket_token} empty='—' />
                    ) : (
                      ctx(selectedRecord).ticket_token ?? '—'
                    )}
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted mb-1'>Топик / Kind</div>
                  <div>
                    {selectedRecord.topic}
                    {selectedRecord.kind ? ` / ${selectedRecord.kind}` : ''}
                  </div>
                </div>
                {selectedRecord.url && (
                  <div className='col-12'>
                    <div className='fw-bold text-muted mb-1'>URL</div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {selectedRecord.url}
                    </div>
                  </div>
                )}
                {selectedRecord.error && (
                  <div className='col-12'>
                    <div
                      style={{
                        border: '1px solid var(--bs-danger)',
                        background: 'rgba(var(--bs-danger-rgb), 0.08)',
                        borderRadius: '6px',
                        padding: '10px 14px',
                        color: 'var(--bs-danger)',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                      }}
                    >
                      {selectedRecord.error}
                    </div>
                  </div>
                )}
              </div>

              <div className='row g-4'>
                <div className='col-md-3'>
                  <div className='fw-bold text-muted mb-1'>Мерчант</div>
                  <div>{ctx(selectedRecord).merchant_name ?? '—'}</div>
                </div>
                <div className='col-md-3'>
                  <div className='fw-bold text-muted mb-1'>ID транзакции</div>
                  <div>{selectedRecord.transaction_id ?? '—'}</div>
                </div>
                <div className='col-md-3'>
                  <div className='fw-bold text-muted mb-1'>Адаптер</div>
                  <div>{ctx(selectedRecord).adapter_name ?? '—'}</div>
                </div>
                <div className='col-md-3'>
                  <div className='fw-bold text-muted mb-1'>ID адаптера</div>
                  <div>{selectedRecord.payment_id ?? '—'}</div>
                </div>
              </div>

              <div className='row g-4'>
                <div className='col-md-6'>
                  <PayloadBlock label='Request' content={selectedRecord.request} />
                </div>
                <div className='col-md-6'>
                  <PayloadBlock label='Response' content={selectedRecord.response} />
                </div>
              </div>

              {selectedRecord.headers && (
                <PayloadBlock label='Headers' content={selectedRecord.headers} height={200} />
              )}

              {selectedRecord.query && selectedRecord.query !== '{}' && (
                <PayloadBlock label='Query params' content={selectedRecord.query} height={150} />
              )}

              {selectedRecord.context && Object.keys(selectedRecord.context).length > 0 && (
                <PayloadBlock
                  label='Context'
                  content={JSON.stringify(selectedRecord.context)}
                  height={200}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => setSelectedRecord(null)}>
            Закрыть
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
